import Dexie, { type Table } from 'dexie';

/**
 * Offline grade queue (spec §7). Single-table Dexie database — pojedyncza tabela
 * `gradeQueue` przechowuje grade'y zrobione offline lub gdy `POST /api/study/grade`
 * sfailowal sieciowo (defensive fallback z `useOfflineGrade`). `sync-controller`
 * flushuje queue na `online` event + 5min heartbeat (spec §5.3).
 *
 * Indices:
 *  - `++id` PK auto-increment (Dexie convention)
 *  - `status` — szybkie `where('status').equals('pending')`
 *  - `createdAt` — sortBy oldest-first
 *  - `[status+createdAt]` — compound dla typowego flush scan (`pending`, oldest-first)
 *
 * `payload` jest exact shape do POST /api/study/grade (camelCase per
 * `apps/api/Fiszki.Api/Api/Study/GradeRequestDto.cs`). `ratedAt` jest persisted
 * jako `Date.now()` epoch ms; flush formatuje do ISO8601 z LOKALNYM offsetem
 * (`tzOffsetMinutes`) przez `toLocalIsoString`, żeby backend kubełkował DailyStats
 * po dacie lokalnej użytkownika — patrz `apps/web/src/lib/datetime.ts`.
 */
export interface GradeQueueItem {
  id?: number;
  payload: {
    cardId: number;
    variant: string;
    rating: 1 | 2 | 3 | 4;
    durationMs: number;
    studySessionId: number;
    idempotencyKey: string;
    ratedAt: number;
    /**
     * Offset użytkownika w minutach NA WSCHÓD od UTC, w momencie nauki (NIE replay).
     * Przy replay rekonstruujemy lokalny ISO używając tego offsetu — żeby reviewy
     * zrobione w innej strefie/DST trafiły do właściwego lokalnego dnia, nie aktualnego.
     */
    tzOffsetMinutes: number;
  };
  status: 'pending' | 'flushing';
  retryCount: number;
  createdAt: number;
}

export class FiszkiOfflineDb extends Dexie {
  gradeQueue!: Table<GradeQueueItem, number>;

  constructor() {
    super('fiszki-offline-v1');
    this.version(1).stores({
      gradeQueue: '++id, status, createdAt, [status+createdAt]',
    });
  }
}

export const db = new FiszkiOfflineDb();
